import { useCallback, useEffect, useState } from 'react';
import { reservacionesService } from '../../services/api';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario, chip } from '../../components/ui/estilos';
import { Campo, Contador, dosColumnas, entrada, tarjetaLista } from './campos';

const DIAS_VISIBLES = 6;
const DOW = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
const iso = (d: Date) => d.toLocaleDateString('en-CA');
const masDias = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };

export const ESTADO_RESERVA: Record<string, { label: string; bg: string; fg: string }> = {
  confirmada: { label: 'Confirmada', bg: 'var(--ok-bg)', fg: 'var(--ok)' },
  completada: { label: 'Llegó', bg: 'var(--well)', fg: 'var(--muted)' },
  no_asistio: { label: 'No asistió', bg: 'var(--cobrar-bg)', fg: 'var(--cobrar-fg)' },
  cancelada:  { label: 'Cancelada', bg: 'var(--danger-bg)', fg: 'var(--danger)' },
};

interface Form { cliente_nombre: string; telefono: string; fecha: string; hora: string; personas: number; mesa_id: string; notas: string }
const deReserva = (r: any): Form => ({
  cliente_nombre: r.cliente_nombre, telefono: r.telefono || '', fecha: String(r.fecha).slice(0, 10),
  hora: String(r.hora).slice(0, 5), personas: r.personas, mesa_id: r.mesa_id, notas: r.notas || '',
});

export default function Reservas({ mesas, say }: { mesas: any[]; say: (m: string) => void }) {
  const [dia, setDia] = useState(iso(new Date()));
  const [lista, setLista] = useState<any[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [nueva, setNueva] = useState(false);

  const cargar = useCallback(() => reservacionesService.getAll(dia)
    .then(r => setLista([...r.data].sort((a, b) => String(a.hora).localeCompare(String(b.hora)))))
    .catch(() => say('No se pudieron cargar las reservaciones.')), [dia, say]);
  useEffect(() => { cargar(); }, [cargar]);

  const sel = lista.find(r => r.id === selId) || null;
  useEffect(() => { setForm(sel ? deReserva(sel) : null); }, [selId, sel?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = sel?.estado === 'confirmada';
  const cambio = !!(sel && form && JSON.stringify(form) !== JSON.stringify(deReserva(sel)));
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm(f => f && { ...f, [k]: v });

  const guardar = async () => {
    if (!sel || !form) return;
    if (!form.cliente_nombre.trim() || !form.hora) { say('Captura cliente y hora.'); return; }
    try {
      await reservacionesService.actualizar(sel.id, { ...form, cliente_nombre: form.cliente_nombre.trim() });
      say('Reservación actualizada.');
      if (form.fecha !== dia) setDia(form.fecha); else cargar();
    } catch (e: any) { say(e.response?.data?.error || 'No se pudo guardar.'); }
  };

  const cerrar = async (accion: 'completada' | 'no_asistio' | 'cancelada') => {
    if (!sel) return;
    if (accion === 'cancelada' && !window.confirm(`¿Cancelar la reservación de ${sel.cliente_nombre}?`)) return;
    try {
      if (accion === 'cancelada') await reservacionesService.cancelar(sel.id);
      else await reservacionesService.cerrar(sel.id, accion);
      say(`Reservación de ${sel.cliente_nombre}: ${ESTADO_RESERVA[accion].label.toLowerCase()}.`);
      cargar();
    } catch (e: any) { say(e.response?.data?.error || 'No se pudo actualizar.'); }
  };

  const mesaSelect = (valor: string, onChange: (v: string) => void, disabled = false) => (
    <select value={valor} onChange={e => onChange(e.target.value)} disabled={disabled} style={entrada} aria-label="Mesa">
      <option value="">Elige mesa…</option>
      {mesas.map(m => <option key={m.id} value={m.id}>Mesa {m.numero} · {m.capacidad} lugares{m.zona ? ` · ${m.zona}` : ''}</option>)}
    </select>
  );

  return (
    <div className="paneles" style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'clamp(280px,34%,400px) minmax(320px,1fr)' }}>
      <aside aria-label="Reservaciones" style={{ borderRight: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: 16, flex: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => setNueva(true)} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>+ Nueva reservación</button>
          <div className="sin-barra" style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
            {Array.from({ length: DIAS_VISIBLES }, (_, i) => masDias(i)).map((d, i) => {
              const on = dia === iso(d);
              return (
                <button key={i} onClick={() => { setDia(iso(d)); setSelId(null); }} aria-pressed={on}
                  style={{ flex: 'none', height: 52, minWidth: 64, padding: '0 12px', borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                    background: on ? 'var(--brand)' : 'var(--surface)', color: on ? 'var(--on-brand)' : 'var(--ink)', border: on ? 'none' : '1px solid var(--line-strong)' }}>
                  <span>{i === 0 ? 'HOY' : DOW[d.getDay()]}</span><span style={{ fontSize: 15 }}>{d.getDate()}</span>
                </button>
              );
            })}
          </div>
          <input type="date" value={dia} onChange={e => { if (e.target.value) { setDia(e.target.value); setSelId(null); } }} aria-label="Otro día" style={{ ...entrada, height: 44 }} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!lista.length && <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: 15 }}>No hay reservaciones ese día.</div>}
          {lista.map(r => {
            const e = ESTADO_RESERVA[r.estado] || ESTADO_RESERVA.confirmada;
            return (
              <button key={r.id} onClick={() => setSelId(r.id)} aria-pressed={selId === r.id} style={tarjetaLista(selId === r.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>{String(r.hora).slice(0, 5)} · {r.cliente_nombre}</span>
                  <span style={{ ...chip(e.bg, e.fg), fontSize: 12, padding: '2px 8px' }}>{e.label}</span>
                </div>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{r.personas} personas · Mesa {r.mesa_numero}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <main style={{ minHeight: 0, overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!sel || !form ? (
          <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige una reservación para ver su detalle.</div>
        ) : <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <input value={form.cliente_nombre} onChange={e => set('cliente_nombre', e.target.value)} disabled={!editable} aria-label="Cliente" maxLength={120}
              style={{ fontSize: 24, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--ink)', padding: '2px 4px', borderRadius: 6, flex: 1, minWidth: 0 }} />
            <span style={{ ...chip(ESTADO_RESERVA[sel.estado].bg, ESTADO_RESERVA[sel.estado].fg), padding: '6px 14px' }}>{ESTADO_RESERVA[sel.estado].label}</span>
          </div>
          <div style={dosColumnas}>
            <Campo label="Teléfono"><input type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} disabled={!editable} maxLength={30} style={entrada} /></Campo>
            <Campo label="Hora"><input type="time" value={form.hora} onChange={e => set('hora', e.target.value)} disabled={!editable} style={entrada} /></Campo>
          </div>
          <div style={dosColumnas}>
            <Campo label="Fecha"><input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={!editable} style={entrada} /></Campo>
            <Campo label="Mesa">{mesaSelect(form.mesa_id, v => set('mesa_id', v), !editable)}</Campo>
          </div>
          <div style={dosColumnas}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>Personas</span>
              {editable ? <Contador label="Personas" valor={form.personas} onCambio={n => set('personas', n)} /> : <div style={{ fontSize: 18, fontWeight: 700 }}>{form.personas}</div>}
            </div>
          </div>
          <Campo label="Notas">
            <textarea value={form.notas} onChange={e => set('notas', e.target.value)} disabled={!editable} placeholder="Ej. mesa junto a la ventana, alergia a mariscos…"
              style={{ ...entrada, height: 'auto', minHeight: 80, padding: '12px 14px', resize: 'vertical' }} />
          </Campo>
          {editable && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {cambio && <button onClick={guardar} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>Guardar cambios</button>}
              <button onClick={() => cerrar('completada')} style={btnSecundario}>Marcar que llegó</button>
              <button onClick={() => cerrar('no_asistio')} style={btnSecundario}>No asistió</button>
              <button onClick={() => cerrar('cancelada')} style={{ ...btnSecundario, color: 'var(--danger)' }}>Cancelar reservación</button>
            </div>
          )}
        </>}
      </main>

      {nueva && <NuevaReserva dia={dia} mesaSelect={mesaSelect} say={say} onCerrar={() => setNueva(false)}
        onCreada={(r) => { setNueva(false); setSelId(r.id); if (String(r.fecha).slice(0, 10) !== dia) setDia(String(r.fecha).slice(0, 10)); else cargar(); }} />}
    </div>
  );
}

function NuevaReserva({ dia, mesaSelect, say, onCerrar, onCreada }: {
  dia: string; mesaSelect: (v: string, f: (v: string) => void) => React.ReactNode; say: (m: string) => void;
  onCerrar: () => void; onCreada: (r: any) => void;
}) {
  const [f, setF] = useState<Form>({ cliente_nombre: '', telefono: '', fecha: dia, hora: '', personas: 2, mesa_id: '', notas: '' });
  const [error, setError] = useState('');
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF(x => ({ ...x, [k]: v }));
  const crear = async () => {
    if (!f.cliente_nombre.trim() || !f.hora || !f.mesa_id) { setError('Captura cliente, hora y mesa.'); return; }
    try {
      const { data } = await reservacionesService.crear({ ...f, cliente_nombre: f.cliente_nombre.trim() });
      say(`Reservación de ${f.cliente_nombre.trim()} creada.`);
      onCreada(data);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo crear la reservación.'); }
  };
  return (
    <Dialogo titulo="Nueva reservación" onCerrar={onCerrar} ancho={460}>
      <input autoFocus placeholder="Nombre del cliente" value={f.cliente_nombre} onChange={e => set('cliente_nombre', e.target.value)} maxLength={120} style={entrada} aria-label="Cliente" />
      <input type="tel" placeholder="Teléfono" value={f.telefono} onChange={e => set('telefono', e.target.value)} maxLength={30} style={entrada} aria-label="Teléfono" />
      <div style={dosColumnas}>
        <input type="date" value={f.fecha} onChange={e => set('fecha', e.target.value)} style={entrada} aria-label="Fecha" />
        <input type="time" value={f.hora} onChange={e => set('hora', e.target.value)} style={entrada} aria-label="Hora" />
      </div>
      <Contador label="Personas" valor={f.personas} onCambio={n => set('personas', n)} />
      {mesaSelect(f.mesa_id, v => set('mesa_id', v))}
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCerrar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
        <button onClick={crear} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16 }}>Guardar reservación</button>
      </div>
    </Dialogo>
  );
}
